import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Fingerprint, FileSearch, Users, Clock, Award, Star, ChevronRight } from "lucide-react";
import api from "../lib/api";
import Layout from "../components/Layout";
import { useContent } from "../contexts/SiteContentContext";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/af7bcd3d-a45b-4c47-b951-d327c90b62cd/images/700d3256c6ce039b4cdd2b429522b1e88f14cc9f6a005904edc967f717fd250b.png";
const EVIDENCE_BG = "https://static.prod-images.emergentagent.com/jobs/af7bcd3d-a45b-4c47-b951-d327c90b62cd/images/4defcba5b9c590eacbf232af01031cda050c13321e1833c61dd9d4e93f97a8d4.png";

export default function HomePage() {
  const [cases, setCases] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [testimonials, setTestimonials] = useState([]);

  const hero = useContent("homepage_hero");
  const feat = useContent("homepage_featured");
  const upc = useContent("homepage_upcoming");
  const how = useContent("homepage_how_it_works");
  const tst = useContent("homepage_testimonials");
  const cta = useContent("homepage_cta");
  const global = useContent("global");

  useEffect(() => {
    api.get("/cases").then((res) => {
      setCases(res.data);
      const feat = res.data.find((c) => c.featured) || res.data[0];
      setFeatured(feat);
    });
    api.get("/testimonials").then((res) => setTestimonials(res.data));
  }, []);

  const upcoming = cases.filter((c) => c.status !== "available");

  return (
    <Layout>
      {/* HERO */}
      <section data-testid="hero-section" className="relative min-h-[92vh] overflow-hidden vignette">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ink-50/95 via-ink-50/80 to-ink-50/40" aria-hidden />
        <div className="absolute inset-0 scanlines" aria-hidden />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 pt-24 md:pt-32 pb-20 grid lg:grid-cols-12 gap-12 items-end min-h-[92vh]">
          <div className="lg:col-span-8 space-y-8 animate-fade-up">
            <div className="flex items-center gap-4 flex-wrap">
              <span className="mono text-[11px] text-bone-muted tracking-[0.35em] uppercase">
                {hero.eyebrow_classification || "CONFIDENCIAL // CLASSIFICAÇÃO 1"}
              </span>
              <span className="mono text-[11px] text-blood tracking-[0.35em] uppercase animate-flicker">
                {hero.eyebrow_status || "● CASO ABERTO"}
              </span>
            </div>

            <h1 className="headline text-[14vw] md:text-[8vw] lg:text-[6.5vw] leading-[0.85]">
              {hero.title_line1 || "Mistérios"} <br />
              <span className="text-blood">{hero.title_red || "para resolver"}</span>
              <br />
              {hero.title_line3 || "em casa."}
            </h1>

            <p className="text-bone-dim text-lg md:text-xl max-w-2xl leading-relaxed">
              {hero.paragraph || "Cada dossier é um caso por resolver."}
            </p>

            <div className="flex flex-wrap items-center gap-4 pt-4">
              <Link to={featured ? `/casos/${featured.slug}` : "/casos"} data-testid="hero-start-investigation-btn">
                <button className="btn-primary group">
                  {hero.cta_primary || "Começar Investigação"}
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </button>
              </Link>
              <Link to="/como-funciona" data-testid="hero-how-it-works-btn">
                <button className="btn-ghost">{hero.cta_secondary || "Como funciona"}</button>
              </Link>
            </div>

            <div className="flex flex-wrap items-center gap-8 pt-10 border-t border-white/10 mt-12 max-w-2xl">
              {(hero.stats || [
                { label: "Casos", value: "3+" },
                { label: "Suspeitos por caso", value: "4-8" },
                { label: "Duração", value: "2-4h" },
                { label: "Avaliação", value: "4.9★" },
              ]).map((s, i) => <Stat key={i} label={s.label} value={s.value} />)}
            </div>
          </div>

          <aside className="lg:col-span-4 hidden lg:block">
            <div className="dossier p-6 backdrop-blur-sm bg-ink-50/60">
              <p className="section-tag mb-4">{hero.sidebar_eyebrow || "Ficheiro do dia"}</p>
              <p className="mono text-[10px] text-bone-faded mb-2">REF: DOSSIER-001-PT</p>
              <h3 className="headline text-3xl mb-3 leading-tight">
                {featured ? featured.title : "A carregar..."}
              </h3>
              {featured && (
                <>
                  <p className="text-sm text-bone-muted leading-relaxed mb-5">{featured.subtitle}</p>
                  <div className="mono text-[10px] text-bone-faded space-y-1 mb-5">
                    <div className="flex justify-between border-b border-white/5 pb-1.5">
                      <span>LOCAL</span><span className="text-bone-dim">{featured.location}</span>
                    </div>
                    <div className="flex justify-between border-b border-white/5 pb-1.5">
                      <span>ANO</span><span className="text-bone-dim">{featured.year}</span>
                    </div>
                    <div className="flex justify-between border-b border-white/5 pb-1.5">
                      <span>JOGADORES</span><span className="text-bone-dim">{featured.players}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>DIFICULDADE</span><span className="text-blood">{featured.difficulty}</span>
                    </div>
                  </div>
                  <Link to={`/casos/${featured.slug}`}>
                    <button className="w-full bg-white/5 hover:bg-white/10 border border-white/15 px-5 py-3 mono text-[11px] uppercase tracking-[0.2em] flex items-center justify-between transition-colors">
                      Abrir dossier <ChevronRight className="w-4 h-4" />
                    </button>
                  </Link>
                </>
              )}
            </div>
          </aside>
        </div>

        {/* Bottom ticker */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-white/10 bg-ink-50/90 backdrop-blur-md">
          <div className="overflow-hidden py-3">
            <div className="flex animate-marquee whitespace-nowrap mono text-[11px] tracking-[0.4em] text-bone-faded">
              {Array(8).fill((global.marquee_text || "CRIMESEMCASA · CONFIDENCIAL · CASO ABERTO ·") + " ").map((t, i) => (
                <span key={i} className="px-6">{t}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED CASE — Caso #001 */}
      {featured && (
        <section data-testid="featured-case-section" className="relative py-24 md:py-32 border-t border-white/10">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
              <div>
                <p className="section-tag mb-4">{featured.code} // EM DESTAQUE</p>
                <h2 className="headline text-5xl md:text-7xl">
                  {featured.title}
                </h2>
              </div>
              <span className="stamp text-sm">Caso aberto</span>
            </div>

            <div className="grid lg:grid-cols-12 gap-8 md:gap-10">
              <div className="lg:col-span-7 relative">
                <div
                  className="aspect-[4/3] bg-cover bg-center relative overflow-hidden"
                  style={{ backgroundImage: `url(${featured.cover_image})` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-ink-50 via-transparent to-transparent" />
                  <div className="absolute top-6 left-6 mono text-[10px] tracking-[0.3em] text-white/80">
                    EVIDÊNCIA #001 — {featured.year}
                  </div>
                  <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                    <p className="text-bone-dim text-sm max-w-md leading-relaxed">{featured.subtitle}</p>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-5 space-y-8">
                <p className="text-bone-dim text-base leading-relaxed">{featured.full_description}</p>

                <div className="grid grid-cols-2 gap-4">
                  <InfoCell icon={<Clock className="w-4 h-4" />} label="Duração" value={featured.duration} />
                  <InfoCell icon={<Users className="w-4 h-4" />} label="Jogadores" value={featured.players} />
                  <InfoCell icon={<Award className="w-4 h-4" />} label="Dificuldade" value={featured.difficulty} />
                  <InfoCell icon={<Fingerprint className="w-4 h-4" />} label="Suspeitos" value={`${featured.suspects?.length || 0}`} />
                  {featured.document_count && (
                    <InfoCell icon={<FileSearch className="w-4 h-4" />} label="Documentos" value={`+${featured.document_count}`} />
                  )}
                  {featured.age_rating && (
                    <InfoCell icon={<Award className="w-4 h-4" />} label="Classificação" value={featured.age_rating} />
                  )}
                </div>

                <div className="flex items-end justify-between pt-6 border-t border-white/10">
                  <div>
                    <p className="mono text-[10px] text-bone-faded tracking-[0.3em] mb-2">{feat.price_label || "PREÇO"}</p>
                    <p className="headline text-4xl text-blood">{featured.price.toFixed(2)}€</p>
                  </div>
                  <Link to={`/casos/${featured.slug}`} data-testid="featured-open-case-btn">
                    <button className="btn-primary">
                      {feat.investigate_button || "Investigar"} <ArrowRight className="w-4 h-4" />
                    </button>
                  </Link>
                </div>
              </div>
            </div>

            {/* Suspects strip */}
            {featured.suspects?.length > 0 && (
              <div className="mt-20">
                <p className="section-tag mb-6">Suspeitos identificados</p>
                <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
                  {featured.suspects.map((s, i) => (
                    <div key={i} className="dossier p-5 group transition-all duration-300 hover:-translate-y-1">
                      <p className="mono text-[10px] text-bone-faded mb-3 tracking-[0.3em]">
                        SUSPEITO {String(i + 1).padStart(2, "0")}
                      </p>
                      <h4 className="headline text-xl mb-2">{s.name}</h4>
                      <p className="mono text-[10px] text-blood mb-3 uppercase tracking-[0.2em]">{s.role}</p>
                      <p className="text-xs text-bone-muted leading-relaxed redacted">
                        {s.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* UPCOMING CASES */}
      <section data-testid="upcoming-cases-section" className="relative py-24 md:py-32 border-t border-white/10 overflow-hidden">
        <div
          className="absolute inset-0 opacity-10 bg-cover bg-center"
          style={{ backgroundImage: `url(${EVIDENCE_BG})` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-ink-50/85" aria-hidden />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="mb-14">
            <p className="section-tag mb-4">Em breve</p>
            <h2 className="headline text-5xl md:text-7xl">Próximos casos</h2>
            <p className="text-bone-muted text-base mt-5 max-w-2xl">
              Cada caso é um novo mistério. Inscreve o teu nome e sê dos primeiros a abrir o dossier.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {upcoming.length === 0 ? (
              <p className="text-bone-muted mono text-sm col-span-full">A preparar os próximos dossiers...</p>
            ) : upcoming.map((c) => (
              <Link key={c.id} to={`/casos/${c.slug}`} data-testid={`upcoming-case-${c.slug}`} className="block group">
                <div className="dossier relative overflow-hidden">
                  <div
                    className="aspect-[4/5] bg-cover bg-center relative transition-transform duration-700 group-hover:scale-105"
                    style={{ backgroundImage: `url(${c.cover_image})` }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-t from-ink-100 via-ink-100/40 to-transparent" />
                    <div className="absolute top-4 left-4 right-4 flex items-start justify-between">
                      <span className="mono text-[10px] tracking-[0.3em] bg-ink-50/80 backdrop-blur px-3 py-1.5">
                        {c.code}
                      </span>
                      <span className="mono text-[10px] tracking-[0.3em] text-blood bg-ink-50/80 backdrop-blur px-3 py-1.5">
                        EM BREVE
                      </span>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <h3 className="headline text-2xl md:text-3xl mb-2 leading-tight">{c.title}</h3>
                      <p className="text-bone-muted text-xs leading-relaxed mb-3">{c.subtitle}</p>
                      <p className="mono text-[10px] text-blood tracking-[0.3em]">
                        {c.release_date || "Lançamento brevemente"}
                      </p>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section data-testid="how-it-works-section" className="py-24 md:py-32 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="mb-14 max-w-2xl">
            <p className="section-tag mb-4">Procedimento</p>
            <h2 className="headline text-5xl md:text-7xl">Como funciona</h2>
            <p className="text-bone-muted mt-5">
              Quatro passos. Uma noite. Um culpado por desmascarar.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
            {[
              { n: "01", t: "Escolhe o caso", d: "Escolhe o caso que queres resolver e faz a compra online." },
              { n: "02", t: "Descarrega o dossier", d: "Recebes acesso imediato ao PDF completo para descarregar e imprimir em casa." },
              { n: "03", t: "Investiga em equipa", d: "Distribui as provas, analisa os testemunhos e liga as pistas. Há armadilhas no meio." },
              { n: "04", t: "Descobre o culpado", d: "Revê todas as evidências, vota no principal suspeito e revela a solução final." },
            ].map((s) => (
              <div key={s.n} className="bg-ink-50 p-8 md:p-10">
                <p className="mono text-[10px] text-blood tracking-[0.3em] mb-6">PASSO {s.n}</p>
                <h3 className="headline text-2xl mb-4">{s.t}</h3>
                <p className="text-bone-muted text-sm leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section data-testid="testimonials-section" className="py-24 md:py-32 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-14 flex-wrap gap-4">
            <div>
              <p className="section-tag mb-4">Testemunhos</p>
              <h2 className="headline text-5xl md:text-7xl">Quem já abriu o caso</h2>
            </div>
            <div className="mono text-xs text-bone-muted tracking-[0.25em]">
              ★★★★★ <span className="ml-2 text-bone-dim">4.9 / 5</span> · <span className="text-bone-faded">214 investigadores</span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.slice(0, 4).map((t) => (
              <div key={t.id} className="dossier p-6 md:p-7">
                <div className="flex gap-0.5 mb-5 text-blood">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <Star key={i} className="w-3.5 h-3.5 fill-current" />
                  ))}
                </div>
                <p className="text-sm leading-relaxed text-bone-dim mb-6">"{t.quote}"</p>
                <div className="border-t border-white/10 pt-4">
                  <p className="mono text-[11px] tracking-[0.2em] uppercase text-white">{t.name}</p>
                  {t.location && <p className="mono text-[10px] text-bone-faded mt-1 tracking-[0.2em]">{t.location}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-24 md:py-32 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <p className="section-tag justify-center mb-6">{cta.eyebrow || "Tens curiosidade?"}</p>
          <h2 className="headline text-5xl md:text-7xl mb-8 max-w-4xl mx-auto leading-[0.95]">
            {cta.title_line1 || "O dossier está"} <span className="text-blood">{cta.title_red || "à tua espera"}</span>{cta.title_line3 || "."}
          </h2>
          <p className="text-bone-muted max-w-2xl mx-auto mb-10">
            {cta.paragraph || "Quem foi? Porquê? Como?"}
          </p>
          <Link to="/casos" data-testid="final-cta-btn">
            <button className="btn-primary mx-auto">
              {cta.button || "Ver todos os casos"} <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>
      </section>
    </Layout>
  );
}

const Stat = ({ label, value }) => (
  <div>
    <p className="headline text-3xl text-white">{value}</p>
    <p className="mono text-[10px] tracking-[0.3em] text-bone-faded mt-1">{label}</p>
  </div>
);

const InfoCell = ({ icon, label, value }) => (
  <div className="dossier p-4">
    <div className="flex items-center gap-2 mb-2 text-bone-faded">
      {icon}
      <span className="mono text-[10px] tracking-[0.25em]">{label}</span>
    </div>
    <p className="headline text-base text-white">{value}</p>
  </div>
);
